import React, { useState, useRef } from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import CenterModal from "@ats/src/components/modals/CenterModal";
import Button from "@ats/src/components/shared/Button";
import FormContainer from "@ats/src/components/forms/FormContainer";
import FormSelect from "@ats/src/components/forms/FormSelect";
import FormInput from "@ats/src/components/forms/FormInput";
import Icon from "@ats/src/components/shared/Icon";

import ProseMirrorEditor from "@shared/ProseMirror/Editor";
import textStyles from "@ats/styles/text";

import {
  useCreateHiringStageAutomation,
  useUpdateHiringStageAutomation,
} from "@shared/queryHooks/useHiringStageAutomation";
import {
  useChannelMessageTemplates,
  useCreateChannelMessageTemplate,
} from "@shared/queryHooks/useChannelMessageTemplate";
import { validateChannelMessageTemplate, validateHiringStageAutomation } from "@shared/lib/validateWithYup";

import { HiringStageAutomation } from "@ats/types/job";
import { trackEvent } from "@shared/lib/posthog";

type HiringStageOption = {
  value: number; // id
  label: string; // name
};

type Props = {
  onCancel: () => void;
  isEditing: boolean;
  passedHiringStageAutomation: HiringStageAutomation;
  job: any;
  hiringStageOptions: Array<HiringStageOption>;
};

function HiringStageAutomationModal(props: Props) {
  const { isEditing, onCancel, passedHiringStageAutomation, job, hiringStageOptions } = props;

  /* Automation form state — preserved across the create-template flow
  --------------------------------------------------------------------- */
  const [stageAutomationState, setStageAutomationState] = useState({
    hiringStageId: passedHiringStageAutomation?.hiringStage?.id || null,
    channelMessageTemplateId: passedHiringStageAutomation?.channelMessageTemplateId || null,
    frequency: passedHiringStageAutomation?.frequency || "",
  });
  const [errors, setErrors] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  /* Inline template-creation state
  --------------------------------------------------------------------- */
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [templateErrors, setTemplateErrors] = useState([]);
  const editorRef = useRef(undefined);

  /* Queries & mutations
  --------------------------------------------------------------------- */
  const { data: channelMessageTemplates } = useChannelMessageTemplates();
  const {
    mutate: createHiringStageAutomation,
    isLoading: isLoadingCreate,
  } = useCreateHiringStageAutomation();
  const {
    mutate: updateHiringStageAutomation,
    isLoading: isLoadingUpdate,
  } = useUpdateHiringStageAutomation();
  const {
    mutate: createChannelMessageTemplate,
    isLoading: isLoadingCreateTemplate,
  } = useCreateChannelMessageTemplate();

  window.logger("%c[HiringStageAutomationModal] render", "color: #a05195", {
    props,
    stageAutomationState,
    isCreatingTemplate,
  });

  /* Derived data
  --------------------------------------------------------------------- */
  const channelMessageTemplateSelectOptions = channelMessageTemplates
    ? channelMessageTemplates.items.map((template) => {
        return { value: template.id, label: template.name };
      })
    : [];

  const selectedTemplate = channelMessageTemplates?.items?.find(
    (template) => template.id === stageAutomationState.channelMessageTemplateId,
  );

  const frequencyOptions = [
    { value: "once", label: "Once" },
    { value: "every_time", label: "Every time" },
  ];

  const stageOptionsWithoutInbox = hiringStageOptions.filter(
    (options) => options.label !== "Inbox",
  );
  const filteredStageOptionsWithoutInbox = stageOptionsWithoutInbox.filter(
    (option) =>
      !job.hiringStages.some(
        (stage) => stage.hiringStageAutomation !== null && option.value === stage.id,
      ),
  );

  const stageOptions = isEditing ? stageOptionsWithoutInbox : filteredStageOptionsWithoutInbox;

  /* Automation form handlers
  --------------------------------------------------------------------- */
  const handleChangeHiringStageAutomation = (name, value) => {
    setStageAutomationState({ ...stageAutomationState, [name]: value });
    setErrors([]);
    setIsDirty(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const [isValid, validationErrors] = await validateHiringStageAutomation(stageAutomationState);

    if (!isValid) {
      setErrors(validationErrors);
    } else {
      isEditing ? handleUpdate() : handleCreate();
    }
  };

  const handleCreate = async () => {
    await createHiringStageAutomation(
      { jobId: job.id, ...stageAutomationState },
      {
        onSuccess: (data) => {
          window.logger("%c[HiringStageAutomationModal] handleCreate onSuccess", "color: #a05195", {
            data,
          });
          trackEvent("automation_created", { job_id: job.id, hiring_stage_id: stageAutomationState.hiringStageId, frequency: stageAutomationState.frequency, channel_message_template_id: stageAutomationState.channelMessageTemplateId });
          onCancel();
        },
        onError: (response: any) => {
          window.logger("%c[HiringStageAutomationModal] handleCreate onError", "color: #FF7602", {
            response,
          });
          if (response.data && response.data.errors) setErrors(response.data.errors);
        },
      },
    );
  };

  const handleUpdate = async () => {
    await updateHiringStageAutomation(
      {
        id: passedHiringStageAutomation.id,
        jobId: job.id,
        ...stageAutomationState,
      },
      {
        onSuccess: (data) => {
          window.logger("%c[HiringStageAutomationModal] handleUpdate onSuccess", "color: #a05195", {
            data,
          });
          onCancel();
        },
        onError: (response: any) => {
          window.logger("%c[HiringStageAutomationModal] handleUpdate onError", "color: #FF7602", {
            response,
          });
          if (response.data && response.data.errors) setErrors(response.data.errors);
        },
      },
    );
  };

  /* Inline template-creation handlers
  --------------------------------------------------------------------- */
  const handleStartCreateTemplate = () => {
    setIsCreatingTemplate(true);
    setNewTemplateName("");
    setTemplateErrors([]);
  };

  const handleDiscardTemplate = () => {
    setIsCreatingTemplate(false);
    setNewTemplateName("");
    setTemplateErrors([]);
  };

  const handleChangeNewTemplateName = (_name, value) => {
    setNewTemplateName(value);
    setTemplateErrors([]);
    setIsDirty(true);
  };

  const handleChangeEditorState = () => {
    setTemplateErrors([]);
    setIsDirty(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();

    const body = editorRef.current?.serializedState();
    const [isValid, validationErrors] = await validateChannelMessageTemplate({
      name: newTemplateName,
      body,
    });

    if (!isValid) {
      setTemplateErrors(validationErrors);
      return;
    }

    createChannelMessageTemplate(
      { name: newTemplateName, body },
      {
        onSuccess: (data) => {
          window.logger(
            "%c[HiringStageAutomationModal] handleSaveTemplate onSuccess",
            "color: #a05195",
            { data },
          );
          setStageAutomationState((prev) => ({
            ...prev,
            channelMessageTemplateId: data.id,
          }));
          setIsCreatingTemplate(false);
          setNewTemplateName("");
          setTemplateErrors([]);
          setIsDirty(true);
        },
        onError: (error: any) => {
          window.logger(
            "%c[HiringStageAutomationModal] handleSaveTemplate onError",
            "color: #FF7602",
            { error },
          );
          const errorData = error?.response?.data || error?.data;
          if (errorData?.errors) {
            setTemplateErrors(errorData.errors);
          }
        },
      },
    );
  };

  /* Button sets — swapped based on isCreatingTemplate
  --------------------------------------------------------------------- */
  const automationButtons = (
    <Styled.ButtonContainer>
      <Button
        name="createJobButton"
        type="submit"
        onClick={handleSubmit}
        loading={isLoadingCreate || isLoadingUpdate}
      >
        {isEditing ? "Update automation" : "Add automation"}
      </Button>
      <Button styleType="secondary" onClick={onCancel}>
        <span>Cancel</span>
      </Button>
    </Styled.ButtonContainer>
  );

  const templateCreationButtons = (
    <Styled.ButtonContainer>
      <Button
        name="saveTemplateButton"
        type="submit"
        onClick={handleSaveTemplate}
        loading={isLoadingCreateTemplate}
      >
        Save template
      </Button>
      <Button styleType="secondary" onClick={handleDiscardTemplate}>
        <span>Discard</span>
      </Button>
    </Styled.ButtonContainer>
  );

  /* Copy
  --------------------------------------------------------------------- */
  const automationInstructions = isEditing ? (
    <>
      Edit the automation for the hiring stage{" "}
      <span>{passedHiringStageAutomation?.hiringStage?.name}</span>. Moving a candidate to the
      selected hiring stage will trigger an email message to the candidate using the chosen message
      template.
    </>
  ) : (
    "Moving a candidate to the selected hiring stage will trigger an email message to the candidate using the chosen message template."
  );

  const templateInstructions =
    "Enter the name and content for a new message template. Once saved, it will be selected for this automation.";

  const headerTitle = isCreatingTemplate
    ? "Create new template"
    : isEditing
    ? "Edit automation"
    : "Add automation";

  /* Render
  --------------------------------------------------------------------- */
  return (
    <CenterModal
      headerTitleText={headerTitle}
      onCancel={onCancel}
      hasUnsavedChanges={isDirty}
      width="40rem"
    >
      <Styled.Instructions>
        {isCreatingTemplate ? templateInstructions : automationInstructions}
      </Styled.Instructions>

      <FormContainer
        errors={isCreatingTemplate ? templateErrors : errors}
        buttons={isCreatingTemplate ? templateCreationButtons : automationButtons}
        onSubmit={isCreatingTemplate ? handleSaveTemplate : handleSubmit}
      >
        {isCreatingTemplate ? (
          <>
            <FormInput
              onChange={handleChangeNewTemplateName}
              name="name"
              placeholder=""
              label="Template name"
              value={newTemplateName}
              errors={templateErrors}
              autoFocus
            />
            <Styled.TemplateInput>
              <ProseMirrorEditor
                ref={editorRef}
                enableLabel
                label="Template body"
                editorId="automation-template-editor"
                enableStaticMenuBar={false}
                enableSelectionToolbar={true}
                enableMessageMailMergeMenuBar={true}
                enableMessageKeys={true}
                onChange={handleChangeEditorState}
                defaultValue=""
              />
            </Styled.TemplateInput>
          </>
        ) : (
          <>
            <FormSelect
              onChange={handleChangeHiringStageAutomation}
              name="hiringStageId"
              label="Hiring stage"
              description={null}
              className=""
              value={stageAutomationState.hiringStageId}
              options={stageOptions}
              errors={errors}
              isClearable={false}
              isDisabled={isEditing}
              autoFocus
            />
            <FormSelect
              onChange={handleChangeHiringStageAutomation}
              name="frequency"
              label="Frequency"
              description="Choose to send the message once or every time a candidate is moved to the stage."
              className=""
              value={stageAutomationState.frequency}
              options={frequencyOptions}
              errors={errors}
              isClearable={false}
            />

            {/* Template select + inline "create new" icon button */}
            <Styled.TemplateSelectRow>
              <Styled.TemplateSelectWrapper>
                <FormSelect
                  onChange={handleChangeHiringStageAutomation}
                  name="channelMessageTemplateId"
                  label="Message template"
                  description="Select an existing message template or create a new one."
                  className=""
                  value={stageAutomationState.channelMessageTemplateId}
                  options={channelMessageTemplateSelectOptions}
                  errors={errors}
                  isClearable={false}
                />
              </Styled.TemplateSelectWrapper>
              <Styled.AddTemplateButton
                type="button"
                onClick={handleStartCreateTemplate}
                aria-label="Create new template"
                title="Create new template"
              >
                <Icon name="plus" />
              </Styled.AddTemplateButton>
            </Styled.TemplateSelectRow>

            {/* Read-only preview of the selected template's rendered body. */}
            {selectedTemplate != null && (
              <Styled.PreviewContainer translate="no" className="notranslate">
                <div dangerouslySetInnerHTML={{ __html: selectedTemplate.body || "" }} />
              </Styled.PreviewContainer>
            )}
          </>
        )}
      </FormContainer>
    </CenterModal>
  );
}

export default HiringStageAutomationModal;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Instructions = styled.p((props) => {
  const t: any = props.theme;
  return css`
    label: HiringStageAutomationModal_Instructions;
    ${[t.mb(5)]}

    span {
      font-weight: 700;
      color: ${t.dark ? t.color.gray[300] : t.color.black};
    }
  `;
});

Styled.ButtonContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: HiringStageAutomationModal_ButtonContainer;
    display: flex;
    > * {
      ${t.mr(3)}

      &:last-of-type {
        ${t.mr(0)}
      }
    }
  `;
});

Styled.TemplateSelectRow = styled.div(() => {
  return css`
    label: HiringStageAutomationModal_TemplateSelectRow;
    display: flex;
    align-items: flex-end;
  `;
});

Styled.TemplateSelectWrapper = styled.div(() => {
  return css`
    label: HiringStageAutomationModal_TemplateSelectWrapper;
    flex: 1;
    min-width: 0;
  `;
});

Styled.AddTemplateButton = styled.button((props) => {
  const t: any = props.theme;
  return css`
    label: HiringStageAutomationModal_AddTemplateButton;
    ${[t.ml(2), t.mb(5), t.rounded.sm]}
    flex-shrink: 0;
    height: 32px;
    width: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid ${t.dark ? t.color.gray[600] : t.color.gray[300]};
    background-color: ${t.dark ? t.color.gray[800] : t.color.white};
    color: ${t.dark ? t.color.gray[300] : t.color.gray[600]};
    cursor: pointer;
    transition: background-color 0.1s ease, color 0.1s ease;

    &:hover {
      background-color: ${t.dark ? t.color.gray[700] : t.color.gray[100]};
      color: ${t.dark ? t.color.gray[200] : t.color.black};
    }

    &:focus-visible {
      outline: 2px solid ${t.color.orange[500]};
      outline-offset: 1px;
    }

    svg {
      width: 16px;
      height: 16px;
    }
  `;
});

/* Mirrors the preview container from ChannelMessageTemplateSelectionModal —
   scrollable read-only box with textStyles applied so rendered markdown
   (headings, lists, blockquotes, mail-merge tokens) displays correctly. */
Styled.PreviewContainer = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: HiringStageAutomationModal_PreviewContainer;
    ${[textStyles, t.p(4), t.pb(0), t.rounded.md]}
    border: 1px solid ${t.dark ? t.color.gray[700] : t.color.gray[200]};
    background-color: ${t.dark ? t.color.gray[700] : t.color.gray[100]};
    color: ${t.dark ? t.color.gray[300] : t.color.black};
    max-height: 16rem;
    overflow-y: auto;
    ${t.dark &&
    `
      blockquote {
        color: ${t.color.gray[400]};
        border-left: 4px solid ${t.color.gray[600]};
      }
    `}

    p {
      span.mail-merge-brackets {
        color: ${t.dark ? t.color.gray[500] : t.color.gray[500]};
      }
    }
  `;
});

/* Same wrapper treatment as ChannelMessageTemplateModal — kills the
   trailing margin on the editor's wrapper and gives the textarea a
   reasonable minimum height. */
Styled.TemplateInput = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: HiringStageAutomationModal_TemplateInput;
    > div {
      ${[t.mb(0)]}
    }
    textarea {
      min-height: 12rem;
    }
  `;
});
